import { awaitLeaveEcommerceDesignerBetterAuthResync } from '~~/layers/base/utils/ecommerceDesignerBetterAuthInjunction'
import { isEcommerceDesignerRoute } from '~~/layers/base/utils/isEcommerceDesignerRoute'

/**
 * False in Capacitor via `vite.define` so the admin store dynamic import is tree-shaken out.
 * Unset on web so logout still clears the generic admin route check cache.
 */
declare const __CRANE_ADMIN_UI_ENABLED__: boolean | undefined

/**
 * Keeps `useProfileStore` aligned with Better Auth: clear when logged out / anonymous, refetch
 * when a real session exists so navbar and other clients read roles without per-mount `useFetch`.
 *
 * Also warms return addresses on sign-in so Account’s “N saved” row and `/profile/addresses`
 * hit the in-memory cache instead of a cold `GET` after the user is already in the app.
 */
export default defineNuxtPlugin(() => {
    const route = useRoute()
    const { isLoggedIn, isSessionPending } = useBetterAuth()
    const profileStore = useProfileStore()
    const datadog = useDatadog()

    watch(
        [isLoggedIn, isSessionPending, () => route.fullPath],
        async (_loggedIn, _pending, previous) => {
            await awaitLeaveEcommerceDesignerBetterAuthResync()

            // TK-13530 injunction — re-enable profile sync when purchase designer uses Better Auth.
            if (isEcommerceDesignerRoute(route)) {
                return
            }
            const pending = isSessionPending.value
            const loggedIn = isLoggedIn.value
            // Pending looks logged-out; do not wipe sessionStorage hydrate on refresh.
            if (pending) {
                return
            }
            const wasLoggedIn = previous?.[0]
            if (!loggedIn) {
                profileStore.clearProfile()
                if (wasLoggedIn && __CRANE_ADMIN_UI_ENABLED__ !== false) {
                    const { useAdminResourceCheckStore } = await import(
                        '~~/layers/base/admin-stores/adminResourceCheck.store'
                    )
                    useAdminResourceCheckStore().clearCheckCache()
                }
                return
            }
            await profileStore.ensureProfileLoaded()
            // Soft-fail: transport/API errors must not become unhandled rejections on offline cold
            // start, but still report to Datadog for observability.
            void profileStore.ensureAddressesLoaded().catch((error: unknown) => {
                const err = error instanceof Error ? error : new Error(String(error))
                datadog.logError(err, {
                    feature: 'profile',
                    action: 'ensureAddressesLoaded',
                    phase: 'auth-sync-plugin',
                })
            })
        },
        { immediate: true }
    )
})
